import { NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { CuttingProduct, PriceListProductBazis, PriceListProductElasticInfo, TypeProductBazis } from 'cloud-shared-lib';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { filter, pluck, takeUntil } from 'rxjs/operators';
import { OrderDetailService } from '../../../order-detail.service';

@Component({
  selector: 'cutting-product-data-form',
  templateUrl: './product-data-form.component.html',
  standalone: true,
  imports: [NgIf, MatInputModule, FormsModule, MatFormFieldModule, ReactiveFormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductDataFormComponent implements OnInit, OnDestroy {
  @Input() product: CuttingProduct;

  productForm: UntypedFormGroup;

  unsubscribe$: Subject<void> = new Subject();

  constructor(private _formBuilder: FormBuilder, private orderDetailService: OrderDetailService) {}

  ngOnInit(): void {
    this.productForm = this._formBuilder.group({
      count: [this.product.count, [Validators.required, Validators.min(1)]],
      width: [this.product.width, [Validators.required]],
      height: [this.product.height, [Validators.required]],
      deep: [this.product.deep, [Validators.required]],
    });

    this.getPriceListProduct();
  }

  setDisabledIsNotElastic(priceListProduct: PriceListProductBazis): void {
    if (!priceListProduct.elastic.IsElastic || priceListProduct.processingMode === 0) {
      this.productForm.disable();
      return;
    }

    this.validateFn(priceListProduct.elastic);

    switch (priceListProduct.typeProduct) {
      case TypeProductBazis.tpMoldings:
        this.productForm.controls.width.disable({ emitEvent: false });
        this.productForm.controls.deep.disable({ emitEvent: false });
        break;

      case TypeProductBazis.tpSquare:
        this.productForm.controls.deep.disable({ emitEvent: false });
        break;
    }

    if (
      !this.availabilityOfElasticity(
        priceListProduct.elastic.Min.Height,
        priceListProduct.elastic.Max.Height,
        priceListProduct.elastic.Step.Height
      )
    ) {
      this.productForm.controls.height.disable({ emitEvent: false });
    }

    if (
      !this.availabilityOfElasticity(
        priceListProduct.elastic.Min.Width,
        priceListProduct.elastic.Max.Width,
        priceListProduct.elastic.Step.Width
      )
    ) {
      this.productForm.controls.width.disable({ emitEvent: false });
    }

    if (
      !this.availabilityOfElasticity(
        priceListProduct.elastic.Min.Deep,
        priceListProduct.elastic.Max.Deep,
        priceListProduct.elastic.Step.Deep
      )
    ) {
      this.productForm.controls.deep.disable({ emitEvent: false });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  private availabilityOfElasticity(min: number, max: number, step: number): boolean {
    return !!(min * max * step);
  }

  private validateFn(elastic: PriceListProductElasticInfo): void {
    if (_.isNil(elastic)) {
      return;
    }

    this.productForm.controls.height.addValidators([
      Validators.min(elastic.Min.Height),
      Validators.max(elastic.Max.Height),
    ]);
    this.productForm.controls.width.addValidators([
      Validators.min(elastic.Min.Width),
      Validators.max(elastic.Max.Width),
    ]);
    this.productForm.controls.deep.addValidators([Validators.min(elastic.Min.Deep), Validators.max(elastic.Max.Deep)]);

    this.productForm.updateValueAndValidity();
  }

  private getPriceListProduct(): void {
    this.orderDetailService
      .getPriceListProductInfo(this.product.priceListProductId)
      .pipe(
        filter(res => !!res && !_.isUndefined(res.result)),
        pluck('result'),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(res => this.setDisabledIsNotElastic(res));
  }
}
